import React, { useState } from "react";
import { Select, Divider, Button, Form } from "antd";
import { useTranslation } from "react-i18next";

import { SingleSelect } from "../../../../customComponent/CustomSelect";
import { OTHER } from "../../constants";
import { sizes } from "../../../../../constant/styles";
import useConditionalValidation from "./utils/useConditionalValidation";
const { Option } = Select;

export const SelectItem = ({
  name,
  label,
  required = false,
  rules = [],
  optionItems,
  placeholder,
  style,
  shouldValidateFields = false,
  fieldProps = {},
  formItemProps = {},
  form,
  alternativeFields,
  onChange,
  otherOptionId,
  ...props
}) => {
  const { t } = useTranslation();
  const [isOpen, setIsOpen] = useState(false);
  useConditionalValidation(name, shouldValidateFields);

  // A singleSelect that declares `alternativeFields` is the "known option or Other" pattern: the visible
  // dropdown (e.g. `selectName`) drives a hidden mapped twin (e.g. `name`) that carries the value to the
  // BE. Selecting a KNOWN option mirrors that value into the twin; selecting "Other" (or clearing) empties
  // the twin so the now-visible free-text input starts fresh and the user's typing lands in the twin. The
  // hand-written sections did this in their own onChange; the JSON engine dropped it, which left the twin
  // `null` on the ordinary known-selection path and made the vendor reject the bundle (MVP-31021, Jehad QA
  // Finding A). The twin lives in the same (sub)section as the select, so its path is the select's path
  // with the last segment swapped for the alternative field name.
  const mirrorTwin = (value) => {
    if (alternativeFields?.length && form && Array.isArray(name)) {
      const twinValue = value && value !== OTHER ? value : undefined;
      const parentPath = name.slice(0, -1);
      alternativeFields.forEach((alternativeField) => {
        form.setFieldValue([...parentPath, alternativeField], twinValue);
      });
    }
  };

  const handleChange = (value, option) => {
    mirrorTwin(value);
    onChange?.(value, option);
  };

  // When `otherOptionId` is set, restore the hand-written form's "Other" affordance: the old select
  // rendered "Other" as a targetable dropdownRender BUTTON (id = otherOptionId) below the known options,
  // not as an in-list option. The e2e specs click that id. Drop the in-list "Other" and render the button
  // instead, matching the old form and GenericPhysicianItem. Opt-in, so selects without an id are
  // unchanged (they keep "Other" as an ordinary option). MVP-31021, Jehad QA.
  const items = otherOptionId ? (optionItems || []).filter((item) => item.value !== OTHER) : optionItems;

  const selectOther = () => {
    if (form && Array.isArray(name)) {
      form.setFieldValue(name, OTHER);
    }
    mirrorTwin(OTHER);
    setIsOpen(false);
  };

  const otherOptionProps = otherOptionId
    ? {
        open: isOpen,
        onDropdownVisibleChange: setIsOpen,
        dropdownRender: (menu) => (
          <>
            {menu}
            <Divider style={{ margin: "8px 0" }} />
            <Button
              id={otherOptionId}
              type="text"
              style={{ width: "100%", textAlign: "start", fontSize: sizes.medium, background: "transparent" }}
              onClick={selectOther}
            >
              {t("other")}
            </Button>
          </>
        )
      }
    : {};

  return (
    <Form.Item name={name} label={label} rules={[{ required }, ...rules]} style={style} {...formItemProps}>
      <SingleSelect placeholder={placeholder} {...fieldProps} {...props} {...otherOptionProps} onChange={handleChange}>
        {items?.map((optionItem, index) => {
          return (
            <Option key={optionItem.value + "_" + index} value={optionItem.value}>
              {optionItem.displayValue}
            </Option>
          );
        })}
      </SingleSelect>
    </Form.Item>
  );
};
